// /stories/: published stories (PRD phase 3; ARCHITECTURE.md section 3 `stories`, section 10).
// Reads the public `stories` collection, newest first, with the lite Firestore SDK (loaded only
// here, lazily). Filter chips by era, connection, and collection are built from what loads; the
// choice is kept in the address (?era=founding) so a filtered view can be shared.
// Audio and photo URLs come from fb-storage downloadUrl(), resolved as each story nears the screen.
// Logs (component "stories"): page_ready, not_configured, load_start, load_ok, load_failed,
// retry, filter_change, filters_cleared, playback_start, playback_error, transcript_open,
// media_url_failed. Ids only (storyId, clipId), never names or transcript text.
import { logger } from '../lib/log.js';
import { isBackendConfigured } from '../lib/config.js';
import { announce } from '../lib/forms.js';
import { errorDetail } from './common.js';

const log = logger('stories');
const MAX_STORIES = 200;
const GROUPS = ['era', 'connection', 'collection'];

/** "3 minutes 12 seconds" (plain words for every reader). */
export function lengthLabel(sec) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (!s) return '';
  const m = Math.floor(s / 60), r = s % 60;
  const parts = [];
  if (m) parts.push(`${m} ${m === 1 ? 'minute' : 'minutes'}`);
  if (r) parts.push(`${r} ${r === 1 ? 'second' : 'seconds'}`);
  return parts.join(' ');
}

function el(tag, attrs = {}, ...children) {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k === 'class') n.className = v;
    else if (k === 'text') n.textContent = v;
    else n.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children) if (c !== null && c !== undefined && c !== false) n.append(c);
  return n;
}

const norm = (s) => String(s || '').trim().toLowerCase();
/** Drop repeats that differ only in case or spacing at the ends (first spelling wins). */
const uniqueNames = (list) => list.filter((c, i) => list.findIndex((x) => norm(x) === norm(c)) === i);

/** Plain words for a failed load. */
function loadError(err) {
  const code = String((err && err.code) || '').replace(/^firestore\//, '');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'Your phone or computer is not connected to the internet right now. Connect to Wi-Fi or mobile data, then press Try again.';
  }
  if (code === 'unavailable' || code === 'deadline-exceeded' || /network|fetch|dynamically imported module|Load failed/i.test(String(err && err.message))) {
    return 'We could not reach our server. Please check your connection, then press Try again.';
  }
  return 'Something went wrong on our end. Please try again in a minute.';
}

export async function init() {
  const root = document.querySelector('[data-stories]');
  if (!root) return;
  const states = Object.fromEntries([...root.querySelectorAll('[data-state]')].map((n) => [n.getAttribute('data-state'), n]));
  const show = (name) => { for (const [k, n] of Object.entries(states)) n.hidden = k !== name; };

  if (!isBackendConfigured()) {
    show('unconfigured');
    log.info('not_configured', {});
    return;
  }

  let meta = { eras: [], connections: [], sessionTitles: [] };
  try { meta = { ...meta, ...JSON.parse(document.getElementById('stories-data').textContent) }; } catch { /* labels fall back to ids */ }
  const eraLabel = Object.fromEntries(meta.eras.map((e) => [e.id, e.label]));
  const connLabel = Object.fromEntries(meta.connections.map((c) => [c.id, c.label]));
  // A collection named after a numbered session's template title ("Opening: who they are", written
  // from the interviewer's side) shows the session's public title instead (selfTitle || title).
  // The page's data block lists only those sessions (see src/stories/index.njk).
  const publicTitle = new Map((meta.sessionTitles || []).map((t) => [norm(t.title), t.selfTitle]));
  const collectionName = (c) => publicTitle.get(norm(c)) || c;

  const listEl = root.querySelector('[data-story-list]');
  const countEl = root.querySelector('[data-result-count]');
  const filtersEl = root.querySelector('[data-filters]');
  const filterEmpty = root.querySelector('[data-filter-empty]');
  let stories = [];
  const params = new URLSearchParams(location.search);
  const active = Object.fromEntries(GROUPS.map((g) => [g, params.get(g) || null]));
  if (active.collection) active.collection = collectionName(active.collection); // an older shared link
  let mediaModule = null;
  const mediaFns = () => (mediaModule = mediaModule || import('../lib/fb-storage.js'));

  // ---------------------------------------------------------------- load
  async function load() {
    show('loading');
    const t0 = performance.now();
    log.info('load_start', {});
    try {
      const { getDb, collection, getDocs, query, orderBy, limit } = await import('../lib/fb-db.js');
      const db = await getDb();
      const snap = await getDocs(query(collection(db, 'stories'), orderBy('publishedAt', 'desc'), limit(MAX_STORIES)));
      stories = snap.docs.map((d) => ({ id: d.id, ...d.data() })).map(clean).filter((s) => s.clips.length);
      log.info('load_ok', { count: stories.length, ms: Math.round(performance.now() - t0) });
      if (!stories.length) { show('empty'); return; }
      buildFilters();
      render();
      show('ready');
    } catch (err) {
      log.error('load_failed', { ...errorDetail(err), ms: Math.round(performance.now() - t0) });
      root.querySelector('[data-error-msg]').textContent = loadError(err);
      show('error');
      const h = root.querySelector('[data-error-h]');
      if (h) h.focus();
    }
  }

  /** Defensive copy of a story document: only the public fields, in the shapes we render. */
  function clean(s) {
    const arr = (v) => (Array.isArray(v) ? v : []);
    return {
      id: s.storyId || s.id,
      displayName: String(s.displayName || 'A St. Theresa storyteller'),
      connection: s.connection || 'other',
      connectionLabel: s.connectionLabel || connLabel[s.connection] || '',
      yearsInvolved: s.yearsInvolved ? String(s.yearsInvolved) : '',
      eras: arr(s.eras).map(String),
      collections: uniqueNames(arr(s.collections).map((c) => collectionName(String(c).trim())).filter(Boolean)),
      photoPath: s.photoPath || null,
      publishedAt: s.publishedAt && typeof s.publishedAt.toDate === 'function' ? s.publishedAt.toDate() : null,
      clips: arr(s.clips).filter((c) => c && c.audioPath).map((c) => ({
        clipId: String(c.clipId || ''), promptText: String(c.promptText || ''), durationSec: c.durationSec,
        audioPath: c.audioPath, mimeType: c.mimeType || '', transcript: String(c.transcript || ''),
      })),
    };
  }

  // ---------------------------------------------------------------- filters
  const indexIn = (arr, id) => { const i = arr.findIndex((x) => x.id === id); return i < 0 ? 999 : i; };

  /** Chip options for a group, with how many stories each one has. */
  function options(group) {
    const map = new Map(); // key -> { id, label, n }
    const add = (key, id, label) => { const o = map.get(key) || { id, label, n: 0 }; o.n++; map.set(key, o); };
    for (const s of stories) {
      if (group === 'era') {
        for (const e of new Set(s.eras)) if (eraLabel[e]) add(e, e, eraLabel[e]);
      } else if (group === 'connection') {
        add(s.connection, s.connection, s.connectionLabel || connLabel[s.connection] || 'Community member');
      } else {
        const seen = new Set();
        for (const c of s.collections) { const k = norm(c); if (!seen.has(k)) { seen.add(k); add(k, c, c); } }
      }
    }
    const list = [...map.values()];
    if (group === 'era') list.sort((a, b) => indexIn(meta.eras, a.id) - indexIn(meta.eras, b.id));
    else if (group === 'connection') list.sort((a, b) => indexIn(meta.connections, a.id) - indexIn(meta.connections, b.id));
    else list.sort((a, b) => a.label.localeCompare(b.label));
    return list;
  }

  const same = (g, a, b) => (g === 'collection' ? norm(a) === norm(b) : a === b);

  function buildFilters() {
    let any = false;
    for (const g of GROUPS) {
      const box = filtersEl.querySelector(`[data-filter="${g}"]`);
      const chips = box.querySelector('[data-chips]');
      const opts = options(g);
      if (active[g] && !opts.some((o) => same(g, o.id, active[g]))) active[g] = null;
      box.hidden = opts.length < 2;
      if (box.hidden) { active[g] = null; continue; }
      any = true;
      const chip = (value, label, n) => {
        const b = el('button', { type: 'button', class: 'chip', 'data-group': g, 'data-value': value || '' });
        b.append(el('span', { text: label }));
        // Shows "Alumni (1)" and reads "Alumni (1) story": the name starts with the visible words,
        // so someone using voice control can say what they see (WCAG 2.5.3).
        if (n) b.append(el('span', { class: 'oh-chip__n', text: ` (${n})` }), el('span', { class: 'sr-only', text: n === 1 ? ' story' : ' stories' }));
        return b;
      };
      chips.replaceChildren(chip('', 'All'), ...opts.map((o) => chip(o.id, o.label, o.n)));
    }
    filtersEl.hidden = !any;
    syncChips();
  }

  function syncChips() {
    filtersEl.querySelectorAll('.chip').forEach((b) => {
      const g = b.getAttribute('data-group');
      const v = b.getAttribute('data-value') || null;
      const on = v === null ? active[g] === null : (active[g] !== null && same(g, v, active[g]));
      b.setAttribute('aria-pressed', String(on));
    });
  }

  function matches(s) {
    if (active.era && !s.eras.includes(active.era)) return false;
    if (active.connection && s.connection !== active.connection) return false;
    if (active.collection && !s.collections.some((c) => norm(c) === norm(active.collection))) return false;
    return true;
  }

  function updateAddress() {
    const p = new URLSearchParams();
    for (const g of GROUPS) if (active[g]) p.set(g, active[g]);
    const q = p.toString();
    try { history.replaceState(null, '', q ? `?${q}` : location.pathname); } catch { /* ignore */ }
  }

  filtersEl.addEventListener('click', (e) => {
    const b = e.target.closest('.chip');
    if (!b) return;
    const g = b.getAttribute('data-group');
    const v = b.getAttribute('data-value') || null;
    active[g] = v;
    log.info('filter_change', { group: g, value: g === 'collection' ? (v ? 'collection' : null) : v });
    syncChips();
    render({ announceCount: true });
    updateAddress();
  });

  root.querySelector('[data-clear-filters]').addEventListener('click', () => {
    GROUPS.forEach((g) => { active[g] = null; });
    log.info('filters_cleared', {});
    syncChips();
    render({ announceCount: true });
    updateAddress();
    const first = filtersEl.querySelector('.chip');
    if (first) first.focus();
  });

  // ---------------------------------------------------------------- render
  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
      for (const en of entries) if (en.isIntersecting) resolveOnce(en.target);
    }, { rootMargin: '800px 0px' })
    : null;
  const resolved = new WeakSet();
  /** Resolve a story's audio and photo URLs once: when it nears the screen, or the moment someone reaches for it. */
  function resolveOnce(art) {
    if (resolved.has(art)) return;
    resolved.add(art);
    if (observer) observer.unobserve(art);
    resolveMedia(art);
  }

  const rendered = new Map(); // story id -> article element (kept so audio state survives filtering)

  function render({ announceCount = false } = {}) {
    const shown = stories.filter(matches);
    const frag = document.createDocumentFragment();
    for (const s of shown) {
      let art = rendered.get(s.id);
      if (!art) {
        art = storyEl(s);
        rendered.set(s.id, art);
        const now = () => resolveOnce(art);
        art.addEventListener('focusin', now);
        art.addEventListener('pointerdown', now);
        if (observer) observer.observe(art); else now();
      }
      frag.append(art);
    }
    listEl.replaceChildren(frag);
    const filtered = GROUPS.some((g) => active[g]);
    countEl.textContent = filtered
      ? `Showing ${shown.length} of ${stories.length} ${stories.length === 1 ? 'story' : 'stories'}.`
      : `${stories.length} ${stories.length === 1 ? 'story' : 'stories'}, newest first.`;
    filterEmpty.hidden = shown.length > 0;
    if (announceCount) announce(countEl.textContent);
  }

  function storyEl(s) {
    const hid = `story-${s.id}-h`;
    const art = el('article', { class: 'oh-story', id: `story-${s.id}`, 'aria-labelledby': hid, 'data-story-id': s.id });
    const head = el('header', { class: 'oh-story__head' });
    if (s.photoPath) {
      const fig = el('div', { class: 'oh-story__photo', 'data-photo-path': s.photoPath });
      head.append(fig);
    }
    const metaBits = [s.connectionLabel, s.yearsInvolved].filter(Boolean).join(' · ');
    const tags = el('ul', { class: 'oh-story__tags', role: 'list' });
    s.eras.forEach((e) => { if (eraLabel[e]) tags.append(el('li', { class: 'tag tag--muted', text: eraLabel[e] })); });
    s.collections.forEach((c) => tags.append(el('li', { class: 'tag', text: c })));
    const added = s.publishedAt ? s.publishedAt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
    head.append(el('div', { class: 'oh-story__who' },
      el('h2', { class: 'oh-story__name', id: hid, text: s.displayName }),
      metaBits ? el('p', { class: 'oh-story__meta', text: metaBits }) : null,
      tags.childElementCount ? tags : null,
      added ? el('p', { class: 'oh-story__added', text: `Shared ${added}` }) : null));
    art.append(head);

    const clips = el('ol', { class: 'oh-clips', role: 'list' });
    s.clips.forEach((c, i) => {
      const qid = `story-${s.id}-q${i + 1}`;
      const audio = el('audio', { controls: true, preload: 'none', 'aria-labelledby': qid, 'data-audio-path': c.audioPath, 'data-clip-id': c.clipId });
      audio.append(document.createTextNode('Your browser cannot play this recording. Please read the transcript below.'));
      audio.addEventListener('play', () => log.info('playback_start', { storyId: s.id, clipId: c.clipId }));
      audio.addEventListener('error', () => {
        if (!audio.getAttribute('src')) return;
        const code = audio.error ? audio.error.code : null;
        log.error('playback_error', { storyId: s.id, clipId: c.clipId, code, mimeType: c.mimeType });
        note.textContent = 'This recording could not play in this browser. The transcript below has every word.';
        note.hidden = false;
      });
      const note = el('p', { class: 'oh-clip__note', role: 'status' });
      note.hidden = true;
      const li = el('li', { class: 'oh-clip' },
        el('h3', { class: 'oh-clip__q', id: qid, text: c.promptText || 'A memory' }),
        audio,
        note,
        lengthLabel(c.durationSec) ? el('p', { class: 'oh-clip__len', text: `Length: ${lengthLabel(c.durationSec)}` }) : null);
      if (c.transcript) {
        const det = el('details', { class: 'oh-clip__transcript' }, el('summary', { text: 'Read the transcript' }));
        const body = el('div', { class: 'oh-clip__text' });
        c.transcript.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean).forEach((p) => body.append(el('p', { text: p })));
        det.append(body);
        det.addEventListener('toggle', () => { if (det.open) log.info('transcript_open', { storyId: s.id, clipId: c.clipId }); });
        li.append(det);
      }
      clips.append(li);
    });
    art.append(clips);
    return art;
  }

  async function resolveMedia(art) {
    let downloadUrl;
    try { ({ downloadUrl } = await mediaFns()); } catch (err) {
      log.error('media_url_failed', { storyId: art.getAttribute('data-story-id'), ...errorDetail(err) });
      return;
    }
    const storyId = art.getAttribute('data-story-id');
    await Promise.all([...art.querySelectorAll('audio[data-audio-path]')].map(async (a) => {
      try { a.src = await downloadUrl(a.getAttribute('data-audio-path')); } catch (err) {
        log.error('media_url_failed', { storyId, clipId: a.getAttribute('data-clip-id'), ...errorDetail(err) });
        const note = a.nextElementSibling;
        if (note) { note.textContent = 'This recording is not available right now. The transcript below has every word.'; note.hidden = false; }
      }
    }));
    const photo = art.querySelector('[data-photo-path]');
    if (photo) {
      try {
        const url = await downloadUrl(photo.getAttribute('data-photo-path'));
        const name = art.querySelector('.oh-story__name');
        photo.append(el('img', { src: url, alt: `Photo shared by ${name ? name.textContent : 'the storyteller'}`, loading: 'lazy', decoding: 'async' }));
      } catch (err) {
        log.warn('media_url_failed', { storyId, photo: true, ...errorDetail(err) });
        photo.remove();
      }
    }
  }

  root.querySelector('[data-retry]').addEventListener('click', () => { log.info('retry', {}); load(); });

  log.info('page_ready', { filters: GROUPS.filter((g) => active[g]) });
  await load();
}
